import * as THREE from 'three';
import {
  STAGE,
  heightAt,
  packageExtras,
  type HeightGrid,
  type PlaatsruimteBlock,
  type ServedWorld,
  type SurfaceBlock,
  type WaterBlock,
} from '@vrlearning/lesson-schema';
import type { Layer, SoundPlan } from '../audio/plan.js';
import {
  BEACH_SOUND,
  FOREST_SOUND,
  ISLAND_SOUND,
  LAB_SOUND,
  MEADOW_SOUND,
  MUSEUM_SOUND,
  POLDER_SOUND,
  SEA_FLOOR_SOUND,
  STREET_SOUND,
  TOWN_SQUARE_SOUND,
  VILLA_SOUND,
} from '../audio/soundscapes.js';
import { buildBirdsBlock } from './blocks/birds.js';
import { buildColourBlock } from './blocks/colour.js';
import { blockFeatures, type StatefulBlock } from './blocks/features.js';
import { buildGrassBlock } from './blocks/grass.js';
import { buildLightBlock } from './blocks/light.js';
import { PartRegistry } from './blocks/parts.js';
import { buildParticlesBlock } from './blocks/particles.js';
import { propPlans } from './blocks/props.js';
import { buildRotateBlock } from './blocks/rotate.js';
import { buildRouteBlock } from './blocks/route.js';
import { buildShowBlock } from './blocks/show.js';
import { buildSwarmBlock } from './blocks/swarm.js';
import { dressSurfaces, surfaceMaterials } from './blocks/surface.js';
import { buildWaterBlock } from './blocks/water.js';
import { sunOf } from './build.js';
import { MANIFEST, hdriFile, worldUrl, type Manifest, type ManifestFile } from './manifest.js';
import type { WorldSource } from './source.js';
import { stageFloorShaped, unlitNow } from './unlit-floor.js';
import type { WorldDefinition } from './world.js';

const hex = (colour: string): number => Number.parseInt(colour.slice(1), 16);

/** The package's own files as manifest entries, stored under their media id (W11). */
function packageManifest(served: ServedWorld): Manifest {
  const files: ManifestFile[] = Object.entries(served.files).map(([name, file]) => {
    const extension = name.slice(name.lastIndexOf('.') + 1);
    const kind =
      extension === 'glb'
        ? 'gltf'
        : extension === 'hdr'
          ? 'hdri'
          : extension === 'ogg'
            ? 'audio'
            : extension === 'bin'
              ? 'bin'
              : 'texture';
    const base: ManifestFile = {
      id: kind === 'texture' ? `${name}_diff_pakket` : name,
      asset: name,
      kind,
      stored: `${file.mediaId}.${extension}`,
      bytes: file.bytes,
      served: true,
      licence: 'pakket',
      worlds: [served.slug],
    };
    if (kind === 'hdri' && served.sky) {
      return {
        ...base,
        width: served.sky.width,
        height: served.sky.height,
        sun: { ...served.sky.sun, linear: [1, 1, 1] },
        horizon: served.sky.horizon,
        zenith: served.sky.zenith,
      };
    }
    if (kind === 'audio') return { ...base, loop: true };
    return base;
  });
  return { version: MANIFEST.version, files: [...files, ...MANIFEST.files] };
}

/** The platform's soundscapes, by the name a package gives (lesson-schema's PLATFORM_SOUNDSCAPES). */
const SOUNDSCAPES: Readonly<Record<string, SoundPlan>> = {
  'volcanic-island': ISLAND_SOUND,
  'science-lab': LAB_SOUND,
  forest: FOREST_SOUND,
  meadow: MEADOW_SOUND,
  polder: POLDER_SOUND,
  beach: BEACH_SOUND,
  villa: VILLA_SOUND,
  'sea-floor': SEA_FLOOR_SOUND,
  'town-square': TOWN_SQUARE_SOUND,
  'modern-street': STREET_SOUND,
  'museum-hall': MUSEUM_SOUND,
};

/**
 * A layer that needs a feature the package does not have (its emitters heard from a feature's place, or
 * following its states) cannot play there: dropped, the rest plays.
 */
function layerFits(layer: Layer, features: ReadonlySet<string>): boolean {
  if (layer.follows && !features.has(layer.follows.feature)) return false;
  return layer.emitters.every(
    (emitter) => !('feature' in emitter) || features.has(emitter.feature),
  );
}

/** The package's sound: a platform soundscape shifted by its volume, or its own loop. */
function soundOf(served: ServedWorld, manifest: Manifest): SoundPlan {
  const sound = served.package.sound;
  if ('soundscape' in sound) {
    const plan = SOUNDSCAPES[sound.soundscape] ?? { layers: [] };
    const features = new Set(Object.keys(served.package.features));
    return {
      layers: plan.layers
        .filter((layer) => layerFits(layer, features))
        .map((layer) => ({ ...layer, level: layer.level + sound.volume })),
    };
  }
  const file = manifest.files.find((f) => f.asset === sound.file && f.kind === 'audio');
  return file
    ? {
        layers: [
          {
            name: 'pakket',
            source: { kind: 'recording', id: file.id },
            level: sound.volume,
            emitters: [],
          },
        ],
      }
    : { layers: [] };
}

/** The placement space block of a package, if it has one. */
const plaatsruimteOf = (pkg: ServedWorld['package']): PlaatsruimteBlock | undefined =>
  pkg.blocks.find((b): b is PlaatsruimteBlock => b.type === 'plaatsruimte');

/** Glass and the like: a mesh drawn see-through, which lets the sun by. */
const seeThrough = (mesh: THREE.Mesh): boolean =>
  [mesh.material].flat().some((m) => m.transparent && m.opacity < 1);

/**
 * The house's own shade, as the code villa's: walls, roof, frames and fittings cast and receive, glass
 * does neither, and the land (`land…`, as the dune) only receives, as the floors (`grond…`) do.
 */
function houseShade(mesh: THREE.Mesh): void {
  const land = mesh.name.startsWith('land');
  const glass = seeThrough(mesh);
  mesh.castShadow = !land && !glass;
  mesh.receiveShadow = !glass;
}

/** A world with rooms (`staged: false`): where its pupils stand and carry, as the villa's plan does (budget.ts). */
function walkableOf(block: PlaatsruimteBlock): Pick<WorldDefinition, 'walkable' | 'shadow'> {
  const extent = (area: readonly (readonly [number, number])[]) => {
    const xs = area.map((p) => p[0]);
    const zs = area.map((p) => p[1]);
    return {
      x: [Math.min(...xs), Math.max(...xs)] as const,
      z: [Math.min(...zs), Math.max(...zs)] as const,
    };
  };
  const rects = block.floors.map((floor) => extent(floor.area));
  const all = extent(block.floors.flatMap((floor) => floor.area));
  const width = all.x[1] - all.x[0];
  const depth = all.z[1] - all.z[0];
  return {
    // The floors' box: what the one shadow map covers, a metre of room round it.
    shadow: {
      centre: [(all.x[0] + all.x[1]) / 2, (all.z[0] + all.z[1]) / 2],
      radius: Math.hypot(width, depth) / 2 + 1,
      height: block.ceiling,
    },
    walkable: {
      rects,
      views: block.rooms.map((room) => room.view.pos),
      // No name starts with NUL: a package without fittings has none.
      fittings: block.fittings ?? '\u0000',
      ceiling: block.ceiling,
    },
  };
}

/**
 * A served, checked package as a world (W11): its sky, sun and haze from wereld.json, the ground its
 * height map, and a `build` that lays its terrain GLBs (`grond…` as ground, the rest as dressing,
 * dressed by its surface blocks) and its blocks, and wires its features. Runs nothing from the package.
 */
export function packageDefinition(
  served: ServedWorld,
  media: Readonly<Record<string, string>>,
  heights: HeightGrid,
): WorldSource {
  const pkg = served.package;
  const manifest = packageManifest(served);
  const byStored = new Map(Object.values(served.files).map((f) => [f.mediaId, f]));
  const urlOf = (stored: string): string => {
    const id = stored.slice(0, stored.lastIndexOf('.'));
    return byStored.has(id) ? (media[id] ?? '') : worldUrl(stored);
  };
  const ground = (x: number, z: number): number =>
    Math.hypot(x, z) <= STAGE.floor ? 0 : heightAt(heights, x, z);
  const extras = packageExtras(pkg);
  const skyAsset = 'platform' in pkg.sky.image ? pkg.sky.image.platform : pkg.sky.image.file;
  // The terrain's meshes that lie as the stage's floor (unlit-floor.ts), from the last build.
  let floors: THREE.Mesh[] = [];

  const definition: WorldDefinition = {
    slug: pkg.slug,
    sound: soundOf(served, manifest),
    sky: {
      hdri: skyAsset,
      seen: pkg.sky.seen,
      exposure: pkg.sky.exposure,
      environment: pkg.sky.environment,
      yaw: pkg.sky.yaw,
      drift: pkg.sky.drift,
    },
    sun: {
      intensity: pkg.sun.intensity,
      colour: hex(pkg.sun.colour),
      ...(pkg.sun.from ? { from: pkg.sun.from } : {}),
    },
    ...(pkg.fog
      ? {
          fog: {
            density: pkg.fog.density,
            ...(pkg.fog.colour ? { colour: hex(pkg.fog.colour) } : {}),
          },
        }
      : {}),
    glow: pkg.glow,
    ...(pkg.background ? { background: hex(pkg.background) } : {}),
    ...(!extras.staged && plaatsruimteOf(pkg) ? walkableOf(plaatsruimteOf(pkg)!) : {}),
    ground,
    unlitFloor: () => floors.some(unlitNow),
    props: propPlans(extras.props),
    build(world) {
      floors = [];
      const sky = hdriFile(skyAsset, manifest);
      const towardsSun = sunOf(definition, manifest);
      const horizon = sky.horizon ?? '#bdc1ca';
      const stateful = new Map<string, StatefulBlock>();
      // The GLBs' meshes by name, for the blocks that act on parts (they bind as the GLBs arrive).
      const parts = new PartRegistry();
      const surfaces = pkg.blocks.filter((b): b is SurfaceBlock => b.type === 'surface');
      // Their textures asked for now, with the world's other files (the lesson waits for those).
      const materials = surfaceMaterials(world, surfaces);
      for (const block of pkg.blocks) {
        const name = `${pkg.slug}-${block.id}`;
        if (block.type === 'water') {
          stateful.set(
            block.id,
            buildWaterBlock(
              world,
              block,
              { ground, towardsSun, horizon, name },
              levelsOf(block, pkg.features),
              parts,
            ),
          );
        } else if (block.type === 'grass') {
          buildGrassBlock(world, block, { ground, name });
        } else if (block.type === 'birds') {
          buildBirdsBlock(world, block, name);
        } else if (block.type === 'deeltjes') {
          stateful.set(block.id, buildParticlesBlock(world, block, name));
        } else if (block.type === 'zwerm') {
          stateful.set(block.id, buildSwarmBlock(world, block, name));
        } else if (block.type === 'draaien') {
          const built = buildRotateBlock(world.root, block, parts);
          world.onFeatureFrame((dt) => built.tick(dt));
          stateful.set(block.id, built);
        } else if (block.type === 'zichtbaar') {
          stateful.set(block.id, buildShowBlock(world.root, block, parts));
        } else if (block.type === 'kleur') {
          stateful.set(block.id, buildColourBlock(block, parts));
        } else if (block.type === 'route') {
          const built = buildRouteBlock(world.root, block, parts);
          world.onFrame((_dt, elapsed) => built.tick(elapsed));
          stateful.set(block.id, built);
        } else if (block.type === 'licht') {
          stateful.set(block.id, buildLightBlock(world, block, name));
        }
        // A plaatsruimte builds nothing: the placement code reads it.
      }
      blockFeatures(
        world,
        Object.fromEntries(
          Object.entries(pkg.features).map(([fname, f]) => [
            fname,
            {
              initial: f.initial,
              seconds: f.seconds,
              states: f.states,
              anchor: anchorOf(f.round, `${pkg.slug}-${fname}`, world),
            },
          ]),
        ),
        stateful,
      );
      // A world with a placement space has a house: its meshes cast and receive (ADR-050).
      const casts = plaatsruimteOf(pkg) !== undefined;
      // The terrain: each GLB as it arrives (WorldLoader), ground and dressing by name.
      for (const file of pkg.terrain.glb) {
        const entry = manifest.files.find((f) => f.asset === file && f.kind === 'gltf')!;
        world.files.add(entry.stored);
        const fetchers = world.fetchers;
        if (!fetchers) continue;
        world.loader.request(
          entry.stored,
          () => fetchers.model(urlOf(entry.stored)),
          (model) => {
            if (!model) return;
            dressSurfaces(world, model, surfaces, materials);
            const meshes: THREE.Mesh[] = [];
            model.traverse((node) => {
              if (node instanceof THREE.Mesh) meshes.push(node as THREE.Mesh);
            });
            model.updateMatrixWorld(true);
            for (const mesh of meshes) {
              // Its place in the GLB, once: the world matrix as its own position, turn and scale.
              mesh.matrixWorld.decompose(mesh.position, mesh.quaternion, mesh.scale);
              mesh.removeFromParent();
              if (mesh.name.startsWith('grond')) world.addGround(mesh);
              else {
                world.addDressing(mesh);
                if (casts) houseShade(mesh);
              }
            }
            parts.add(meshes);
            // A ground-projected photo (painted unlit by its kleur block just now) shows no sun shadow.
            for (const mesh of meshes) if (stageFloorShaped(mesh)) floors.push(mesh);
          },
        );
      }
    },
  };
  const credits = pkg.credits.map((credit) => ({
    what: `Wereld ${credit.file}`,
    credit: `${credit.title} door ${credit.author}, ${credit.licence}`,
  }));
  return { kind: 'pakket', definition, manifest, urlOf, credits, servedPackage: pkg };
}

/** The lowest and highest level any feature gives a water block: how far its surf climbs. */
function levelsOf(
  block: WaterBlock,
  features: ServedWorld['package']['features'],
): [number, number] {
  const levels = [block.level];
  for (const feature of Object.values(features)) {
    for (const changes of Object.values(feature.states)) {
      const level = changes[`${block.id}.level`];
      if (typeof level === 'number') levels.push(level);
    }
  }
  return [Math.min(...levels), Math.max(...levels)];
}

/** A feature's invisible anchor where its `round` is (what lessons look at; build.test holds code worlds' to WORLDS). */
function anchorOf(
  round: { centre: readonly [number, number, number]; radius: number; height: number },
  name: string,
  world: { addDressing<T extends THREE.Object3D>(o: T): T },
): THREE.Object3D {
  const anchor = new THREE.Mesh(
    new THREE.CylinderGeometry(round.radius, round.radius, round.height * 2, 12),
    new THREE.MeshBasicMaterial(),
  );
  anchor.position.set(...round.centre);
  anchor.visible = false;
  anchor.name = name;
  return world.addDressing(anchor);
}
